import Alert from '@mui/material/Alert';
import { t } from 'i18next';
import { forwardRef, useCallback, useMemo, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import {
  SettlementFormFields,
  SettlementFormFieldsHandler,
} from '~formsShared/components/SettlementFormFields';
import { Settlement, SettlementLine } from '~hooks/useSettlements/models';

interface DialogState {
  isBusy: boolean;
  isOpen: boolean;
  settlement?: Settlement;
}

export function useSettlementForm() {
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [dialogState, setDialogState] = useState<DialogState>({
    isBusy: false,
    isOpen: false,
  });

  const updateSelectedInvoices = useCallback((invoiceIds: string[]) => {
    setSelectedInvoices(invoiceIds);
  }, []);

  const openDialog = useCallback((settlement?: Settlement) => {
    setDialogState({ isOpen: true, isBusy: false, settlement });
  }, []);

  const closeDialog = useCallback(() => {
    setDialogState((state) => ({ ...state, isOpen: false, isBusy: false }));
  }, []);

  const updateBusyState = useCallback((isBusy: boolean) => {
    setDialogState((state) => ({ ...state, isBusy }));
  }, []);

  const dialogHandler = useMemo(() => {
    return { open: openDialog, close: closeDialog, updateBusyState, ...dialogState };
  }, [openDialog, closeDialog, updateBusyState, dialogState.isBusy, dialogState.isOpen]);

  return { selectedInvoices, updateSelectedInvoices, dialogHandler };
}

interface SettlementFormProps {
  invoiceIds?: string[];
  lines?: SettlementLine[];
  isBusy: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  settlement?: Settlement;
  validationError?: string;
}

export type SettlementFormHandler = SettlementFormFieldsHandler;

export const SettlementForm = forwardRef<SettlementFormHandler, SettlementFormProps>(
  function SettlementForm(
    { invoiceIds, isBusy, isOpen, lines, onClose, onSubmit, settlement, validationError },
    ref,
  ) {
    const isEditing = Boolean(settlement?.id);
    const hasValidationError = Boolean(validationError);
    const [isWaitingForPaymentTerms, setIsWaitingForPaymentTerms] = useState(false);

    return (
      <Dialog isOpen={isOpen} onClose={onClose} preventCloseOnBackdropClick>
        <DialogHeader onClose={onClose}>
          {t('approvals.driver_pay.settlements.configure_settlement')}
        </DialogHeader>

        <DialogContent>
          <SettlementFormFields
            ref={ref}
            invoiceIds={invoiceIds}
            lines={lines}
            onPaymentTermsLoadingChange={setIsWaitingForPaymentTerms}
            settlement={settlement}
          />

          {hasValidationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {validationError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isBusy}
            onClick={onClose}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            disabled={isBusy || hasValidationError || isWaitingForPaymentTerms}
            loading={isBusy}
            onClick={onSubmit}
          >
            {isEditing ? t('actions.update') : t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    );
  },
);
